import { Box, Text } from '@bonanza-social/moonlight';
import { memo, useCallback, useMemo, useRef } from 'react';
import { FlatList } from 'react-native-gesture-handler';
import StatusPerk from './StatusPerk';
import { useSelector } from '@bonanza-social/toolkit/state';
import { selectPerksListForStatus, StatusPerkListItem } from '../../state';
import { StatusPerkListProps } from './types';
import { ListRenderItem, View } from 'react-native';
import { statusPerkRewardTypeMap } from '../../content/perks.content';
import { useListNavigation } from '@bonanza-social/navigation';

function StatusPerkList({ currentStatus, nextStatus }: StatusPerkListProps) {
  const perksList = useSelector((state) =>
    selectPerksListForStatus(state, { currentStatus, nextStatus }),
  );

  const listRef = useRef<FlatList>(null);
  const containerRef = useRef<View>(null);

  const visiblePerks = useMemo(
    () =>
      perksList?.filter((perk) => {
        const perkRewardType = statusPerkRewardTypeMap[perk.perkType];
        return perkRewardType !== 'Hidden';
      }),
    [perksList],
  );

  const { handleScroll, handleLayout, handleContentSizeChange } =
    useListNavigation({
      listRef,
      containerRef,
      itemWidth: undefined,
      showArrows: false,
      itemsCount: visiblePerks ? visiblePerks.length : 0,
      enableDragging: true,
      enableArrows: false,
    });

  const renderPerk = useCallback<ListRenderItem<StatusPerkListItem>>(
    ({ item: perk }) => (
      <StatusPerk
        key={`${perk.currentStatus}-${perk.upgradeType}-${perk.perkType}`}
        {...perk}
      />
    ),
    [],
  );

  const renderSeparator = useCallback(() => <Box width="space-200" />, []);

  const keyExtractor = useCallback(
    (perk: StatusPerkListItem, index: number) =>
      `${perk.currentStatus}-${perk.upgradeType}-${perk.perkType}-${index}`,
    [],
  );

  if (!visiblePerks) return null;

  return (
    <Box gap="space-300" width="100%" flex={1}>
      <Text
        variant="h6"
        locNS="status"
        locKey="yourPerksTitle"
        color="darkSurfacePrimary"
      />
      <View ref={containerRef} onLayout={handleLayout}>
        <FlatList
          ref={listRef}
          data={visiblePerks}
          renderItem={renderPerk}
          keyExtractor={keyExtractor}
          onScroll={handleScroll}
          onContentSizeChange={handleContentSizeChange}
          ItemSeparatorComponent={renderSeparator}
          scrollEventThrottle={16}
          horizontal
          showsHorizontalScrollIndicator={false}
        />
      </View>
    </Box>
  );
}

export default memo(StatusPerkList);
